/** Scope picker — grouped by resource category with select all per group */

import { SCOPE_CATEGORIES } from '@agentwiki/shared'
import { cn } from '../../lib/utils'
import { getWebFeatureProfile } from '../../lib/feature-flags'

interface ScopePickerProps {
  scopes: string[]
  onChange: (scopes: string[]) => void
  isDark: boolean
}

export function ScopePicker({ scopes, onChange, isDark }: ScopePickerProps) {
  const profile = getWebFeatureProfile()
  const categories = Object.entries(SCOPE_CATEGORIES)
    .filter(([key]) => profile.storageSpace || key !== 'storage')
    .map(([key, category]) => [
      key,
      key === 'secret' && !profile.secretsAdvanced
        ? { ...category, scopes: category.scopes.filter((scope) => ['secret:read', 'secret:create', 'secret:update'].includes(scope)) }
        : category,
    ] as const)
  const categoryMap = new Map(categories)

  const toggleScope = (scope: string) => {
    onChange(scopes.includes(scope) ? scopes.filter((s) => s !== scope) : [...scopes, scope])
  }

  const toggleCategory = (categoryKey: string) => {
    const cat = categoryMap.get(categoryKey)
    if (!cat) return
    const allSelected = cat.scopes.every((s) => scopes.includes(s))
    if (allSelected) {
      onChange(scopes.filter((s) => !(cat.scopes as readonly string[]).includes(s)))
    } else {
      onChange([...new Set([...scopes, ...cat.scopes])])
    }
  }

  const isCategorySelected = (categoryKey: string) => {
    const cat = categoryMap.get(categoryKey)
    return cat ? cat.scopes.every((s) => scopes.includes(s)) : false
  }

  return (
    <div className="mt-1.5 space-y-2.5">
      {categories.map(([key, cat]) => (
        <div key={key}>
          <div className="flex items-center gap-2 mb-1">
            <span className={cn('text-[10px] font-semibold uppercase tracking-wide', isDark ? 'text-neutral-500' : 'text-neutral-400')}>
              {cat.label}
            </span>
            <button
              type="button"
              onClick={() => toggleCategory(key)}
              className={cn('text-[10px] cursor-pointer', isDark ? 'text-brand-400 hover:text-brand-300' : 'text-brand-600 hover:text-brand-700')}
            >
              {isCategorySelected(key) ? 'Deselect all' : 'Select all'}
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {cat.scopes.map((s) => (
              <label key={s} className="flex cursor-pointer items-center gap-1.5">
                <input
                  type="checkbox"
                  checked={scopes.includes(s)}
                  onChange={() => toggleScope(s)}
                  className="h-3.5 w-3.5 rounded accent-brand-500"
                />
                <span className={cn('text-[11px]', isDark ? 'text-neutral-300' : 'text-neutral-600')}>{s}</span>
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}
